import { useMemo, useReducer, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  ComposedModal,
  DismissibleTag,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NumberInput,
  Search,
} from '@carbon/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import moment from 'moment';
import { ColorSwatch, DEFAULT_COLORS } from 'components/color-picker';
import { DatePicker } from 'components/date-picker/date-picker';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { PasteListControl } from 'components/paste-list';
import { format0 } from 'utils/number';
import { getDateString } from 'utils/dates';
import { formatRangeSummary } from './utils';
import type {
  AttributeFilterApply,
  FilterAttributeOption,
  FilterKind,
  FilterRange,
  FilterSelection,
} from './types';
import styles from './attribute-filter-modal.module.scss';

/**
 * One attribute's in-progress selection in the modal — categorical values OR a
 * range, plus (color mode only) the per-value / per-range colors and the rule's
 * `applyTo` targets. The color fields stay undefined in filter mode.
 */
interface AttributeDraft {
  selectedValues?: string[];
  range?: FilterRange;
  valueColors?: Record<string, string>;
  rangeColor?: string;
  applyTo?: string[];
}

/** The modal's working selections, keyed by attribute name. */
type DraftMap = Record<string, AttributeDraft>;

/**
 * Display fallback for an attribute whose caller supplies no `valueLabel`: the
 * stored value is shown verbatim, which is right for operators, formations and
 * every other facet whose values are already the words a user reads.
 *
 * @param {string} value - The stored facet value.
 * @return {string} The same value, unchanged.
 */
const identity = (value: string): string => value;

interface ModalState {
  /** The attribute whose editor is shown, or null when there are no attributes. */
  active: string | null;
  draft: DraftMap;
}

type ModalAction =
  | { type: 'activate'; attribute: string }
  | {
      type: 'setValues';
      attribute: string;
      values: string[];
      valueColors?: Record<string, string>;
    }
  | {
      type: 'setRange';
      attribute: string;
      range: FilterRange;
      rangeColor?: string;
    }
  | { type: 'setValueColor'; attribute: string; value: string; color: string }
  | { type: 'setRangeColor'; attribute: string; color: string }
  | { type: 'setApplyTo'; attribute: string; applyTo: string[] }
  | { type: 'clearActive' }
  | { type: 'reset' };

/**
 * True when a range bound is actually set (open-ended ranges may omit a side).
 *
 * @param {number | string} [bound] - The min or max bound.
 * @return {boolean} True when the bound is present (not undefined / empty string).
 */
const isBoundSet = (bound?: number | string): boolean =>
  bound !== undefined && bound !== '';

/**
 * Whether a draft entry holds something appliable — ≥1 categorical value or ≥1
 * range bound. Drives the per-attribute "has selection" dot and the Apply gating.
 *
 * @param {AttributeDraft} draft - One attribute's working selection.
 * @return {boolean} True when it has a value or a range bound.
 */
function hasSelection(draft: AttributeDraft): boolean {
  if (draft.selectedValues && draft.selectedValues.length > 0) {
    return true;
  }
  return (
    Boolean(draft.range) &&
    (isBoundSet(draft.range?.min) || isBoundSet(draft.range?.max))
  );
}

/**
 * True when a range has BOTH bounds set and the lower exceeds the upper — an
 * invalid range the user must fix before applying. Open-ended ranges (one side
 * left blank) are always valid. Numeric bounds compare numerically; ISO date
 * bounds (`yyyy-MM-dd`) compare lexically, which matches chronological order.
 *
 * @param {FilterRange | undefined} range - The working range.
 * @param {FilterKind} kind - The active attribute's editor kind.
 * @return {boolean} True when min > max with both bounds present.
 */
function isRangeInvalid(
  range: FilterRange | undefined,
  kind: FilterKind
): boolean {
  if (!range || !isBoundSet(range.min) || !isBoundSet(range.max)) {
    return false;
  }
  if (kind === 'numericRange') {
    return Number(range.min) > Number(range.max);
  }
  if (kind === 'dateRange') {
    return String(range.min) > String(range.max);
  }
  return false;
}

/**
 * Seeds the draft map from the dataset's currently-applied selections, so the
 * modal opens pre-loaded in edit mode.
 *
 * @param {FilterSelection[]} initialSelections - The dataset's current selections.
 * @return {DraftMap} The seeded draft map.
 */
function seedDraft(initialSelections: FilterSelection[]): DraftMap {
  const draft: DraftMap = {};
  initialSelections.forEach((selection) => {
    draft[selection.attribute] = {
      selectedValues: selection.selectedValues,
      range: selection.range,
      valueColors: selection.valueColors,
      rangeColor: selection.rangeColor,
      applyTo: selection.applyTo,
    };
  });
  return draft;
}

/**
 * Reducer for the modal's local draft (discarded on Cancel). `setValues` /
 * `setRange` replace the active attribute's selection; `clearActive` empties it
 * (which, for a pre-loaded attribute, becomes a removal on Apply); `reset` empties
 * every attribute.
 *
 * @param {ModalState} state - The current modal state.
 * @param {ModalAction} action - The transition.
 * @return {ModalState} The next modal state.
 */
function reducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'activate':
      return { ...state, active: action.attribute };
    // An attribute is exactly one kind, so `setValues` / `setRange` each own their
    // half (a categorical attr never carries a range, and vice-versa). They merge
    // OVER the prior entry rather than replacing it, so the orthogonal color-mode
    // `applyTo` survives a value/range edit. In color mode the action also carries
    // the recomputed `valueColors` / `rangeColor` (the handler auto-assigns defaults
    // and preserves prior picks); in filter mode those are undefined.
    case 'setValues':
      return {
        ...state,
        draft: {
          ...state.draft,
          [action.attribute]: {
            ...state.draft[action.attribute],
            selectedValues: action.values,
            valueColors: action.valueColors,
          },
        },
      };
    case 'setRange':
      return {
        ...state,
        draft: {
          ...state.draft,
          [action.attribute]: {
            ...state.draft[action.attribute],
            range: action.range,
            rangeColor: action.rangeColor,
          },
        },
      };
    // Color-mode overrides: a single swatch pick (per value or for the range) and
    // the "Apply to" target toggle — each updates one field, preserving the rest.
    case 'setValueColor':
      return {
        ...state,
        draft: {
          ...state.draft,
          [action.attribute]: {
            ...state.draft[action.attribute],
            valueColors: {
              ...state.draft[action.attribute]?.valueColors,
              [action.value]: action.color,
            },
          },
        },
      };
    case 'setRangeColor':
      return {
        ...state,
        draft: {
          ...state.draft,
          [action.attribute]: {
            ...state.draft[action.attribute],
            rangeColor: action.color,
          },
        },
      };
    case 'setApplyTo':
      return {
        ...state,
        draft: {
          ...state.draft,
          [action.attribute]: {
            ...state.draft[action.attribute],
            applyTo: action.applyTo,
          },
        },
      };
    case 'clearActive':
      return state.active
        ? { ...state, draft: { ...state.draft, [state.active]: {} } }
        : state;
    case 'reset':
      return { ...state, draft: {} };
    default:
      return state;
  }
}

/**
 * Left pane: a searchable attribute list. The active attribute is highlighted; an
 * attribute with a non-empty selection shows a dot.
 *
 * @param {object} props - The attributes, the active one, the draft, and the activate handler.
 * @return {JSX.Element} The attribute list.
 */
function AttributeList({
  attributes,
  active,
  draft,
  onActivate,
}: {
  attributes: FilterAttributeOption[];
  active: string | null;
  draft: DraftMap;
  onActivate: (attribute: string) => void;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const visible = term
    ? attributes.filter((item) => item.label.toLowerCase().includes(term))
    : attributes;

  return (
    <div className={styles.attributeList}>
      <Search
        size="sm"
        labelText="Search filter names"
        placeholder="Search Filter Names"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <ul className={styles.attributeItems}>
        {visible.map((item) => {
          return item.disabled ? (
            // A disabled attribute (e.g. eval-scoped — the caller's reason) is
            // non-interactive: a full-width span carrying the SAME row styling as
            // the enabled button, so the bottom divider still spans the list. The
            // reason shows via FloatingTooltip — portaled to <body>, so it escapes
            // this list's overflow scroll container (an inline Carbon/native tooltip
            // clips there). `asChild` merges the hover/focus handlers onto the span
            // itself, adding NO wrapper that would shrink the row.
            <li key={item.attribute}>
              <FloatingTooltip
                content={item.disabledReason}
                placement="right"
                asChild
              >
                <span
                  className={classNames(
                    styles.attributeButton,
                    styles.attributeButtonDisabled
                  )}
                  aria-disabled="true"
                  tabIndex={0}
                >
                  <span className={styles.attributeButtonLabel}>
                    {item.label}
                  </span>
                </span>
              </FloatingTooltip>
            </li>
          ) : (
            <li key={item.attribute}>
              <button
                type="button"
                className={classNames(styles.attributeButton, {
                  [styles.attributeButtonActive]: item.attribute === active,
                })}
                onClick={() => onActivate(item.attribute)}
              >
                <span className={styles.attributeButtonLabel}>
                  {item.label}
                </span>
                {hasSelection(draft[item.attribute] ?? {}) && (
                  <span
                    className={styles.selectedDot}
                    aria-label="has selection"
                  />
                )}
              </button>
            </li>
          );
        })}
        {visible.length === 0 && (
          <li className={styles.emptyHint}>No matching attributes</li>
        )}
      </ul>
    </div>
  );
}

/**
 * Categorical value editor: a value search + Select all + Paste List + a checkbox
 * list over the attribute's values.
 *
 * Every value here is the RAW stored value — it's the checkbox id, what `selected`
 * holds and what `onChange` emits. `valueLabel` only changes the text rendered, so
 * an attribute whose stored vocabulary isn't human (`shut_in`) reads properly
 * without the selection drifting from what the backend expects.
 *
 * @param {object} props - The available values, the selected values, and the change handler.
 * @return {JSX.Element} The categorical editor.
 */
function CategoricalEditor({
  label,
  values,
  selected,
  onChange,
  valueLabel,
  hideBulkSelect = false,
}: {
  /** The attribute's display label, e.g. "UWI". */
  label: string;
  values: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Display text for one raw value. Identity when the caller supplies none. */
  valueLabel: (value: string) => string;
  /**
   * Hide the bulk selection controls, Select all and Paste List. Set in color
   * mode, where values are colored one at a time.
   */
  hideBulkSelect?: boolean;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  // Memoized so the filter scan doesn't re-run on every render of the virtualized
  // list (which re-renders on scroll) — value lists can run to the thousands.
  // Matches the label as well as the raw value, so searching a relabelled
  // attribute for what's on screen ("shut in") finds the row.
  const visible = useMemo(() => {
    return term
      ? values.filter(
          (value) =>
            value.toLowerCase().includes(term) ||
            valueLabel(value).toLowerCase().includes(term)
        )
      : values;
  }, [values, term, valueLabel]);
  // Set-backed membership — value lists can run to the thousands, so the
  // per-row checked check and the Select-all scan stay O(1) per lookup.
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visibleSet = useMemo(() => new Set(visible), [visible]);
  const allVisibleSelected = useMemo(
    () =>
      visible.length > 0 && visible.every((value) => selectedSet.has(value)),
    [visible, selectedSet]
  );
  // The id stays the raw value (it's what `onApply` returns and what the backend
  // matches on); the name is what the list shows. `matchPastedList` accepts either,
  // so a user can paste `shut_in` or "Shut In" and still select the same option.
  // Memoized for the same reason as `visible`.
  const pasteOptions = useMemo(
    () => values.map((value) => ({ id: value, name: valueLabel(value) })),
    [values, valueLabel]
  );

  const toggleAll = () => {
    if (allVisibleSelected) {
      onChange(selected.filter((value) => !visibleSet.has(value)));
    } else {
      onChange([...new Set([...selected, ...visible])]);
    }
  };

  // Virtualize the value list — a categorical attribute (operators, formations…)
  // can carry hundreds/thousands of values, so only the rows in view are mounted.
  const listRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: visible.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 32,
    overscan: 12,
  });

  return (
    <>
      <Search
        size="sm"
        labelText="Search values"
        placeholder="Search values..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className={styles.selectRow}>
        {!hideBulkSelect && (
          <button
            type="button"
            className={styles.linkButton}
            onClick={toggleAll}
          >
            {allVisibleSelected ? 'Clear all' : 'Select all'}
          </button>
        )}
        <span className={styles.resultCount}>
          {format0(visible.length)} values
        </span>
      </div>
      {!hideBulkSelect && (
        <PasteListControl
          id={`afm-${label}`}
          label={label}
          options={pasteOptions}
          selectedIds={selected}
          onApply={onChange}
        />
      )}
      {visible.length === 0 ? (
        <p className={styles.emptyHint}>No values</p>
      ) : (
        <div ref={listRef} className={styles.optionList}>
          <div
            className={styles.optionListInner}
            style={{ height: rowVirtualizer.getTotalSize() }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const value = visible[virtualRow.index];
              return (
                <div
                  key={value}
                  className={styles.optionRow}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <Checkbox
                    id={`afm-value-${virtualRow.index}`}
                    labelText={valueLabel(value)}
                    checked={selectedSet.has(value)}
                    onChange={(_event, { checked }: { checked: boolean }) =>
                      onChange(
                        checked
                          ? [...selected, value]
                          : selected.filter((other) => other !== value)
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Numeric range editor: Min + Max inputs. Either bound may be left blank
 * (open-ended); a blank input clears that side of the range.
 *
 * @param {object} props - The current range and the change handler.
 * @return {JSX.Element} The numeric range editor.
 */
function NumericRangeEditor({
  range,
  invalid,
  onChange,
}: {
  range: FilterRange;
  invalid?: boolean;
  onChange: (range: FilterRange) => void;
}) {
  const toBound = (value: number | string): number | undefined => {
    return value === '' ? undefined : Number(value);
  };

  return (
    <>
      {invalid && (
        <p className={styles.rangeError} role="alert">
          Min must be less than or equal to Max
        </p>
      )}
      <div className={styles.rangeRow}>
        <NumberInput
          id="afm-range-min"
          label="Min"
          allowEmpty
          invalid={invalid}
          value={range.min ?? ''}
          onChange={(_event, { value }) =>
            onChange({ ...range, min: toBound(value) })
          }
        />
        <NumberInput
          id="afm-range-max"
          label="Max"
          allowEmpty
          invalid={invalid}
          value={range.max ?? ''}
          onChange={(_event, { value }) =>
            onChange({ ...range, max: toBound(value) })
          }
        />
      </div>
    </>
  );
}

/**
 * Date range editor: From + To pickers, constrained so From ≤ To. Either side may
 * be left blank (open-ended).
 *
 * @param {object} props - The current range and the change handler.
 * @return {JSX.Element} The date range editor.
 */
function DateRangeEditor({
  range,
  invalid,
  onChange,
}: {
  range: FilterRange;
  invalid?: boolean;
  onChange: (range: FilterRange) => void;
}) {
  const min = typeof range.min === 'string' ? range.min : undefined;
  const max = typeof range.max === 'string' ? range.max : undefined;
  const toIso = (dates: Date[]): string | undefined => {
    return dates[0] ? getDateString(dates[0]) : undefined;
  };
  // The DatePicker wrapper runs flatpickr with `dateFormat="m/d/Y"` and forwards
  // min/maxDate untransformed, so a yyyy-MM-dd string is misparsed and the From ≤ To
  // bound silently breaks. Pass a Date (flatpickr's unambiguous type) instead.
  const toBoundDate = (iso?: string): Date | undefined => {
    return iso ? moment(iso, 'YYYY-MM-DD').toDate() : undefined;
  };

  return (
    <>
      {invalid && (
        <p className={styles.rangeError} role="alert">
          From must be on or before To
        </p>
      )}
      <div className={styles.rangeRow}>
        <DatePicker
          id="afm-range-from"
          labelText="From"
          value={min}
          maxDate={toBoundDate(max)}
          invalid={invalid}
          onChange={(dates: Date[]) =>
            onChange({ ...range, min: toIso(dates) })
          }
        />
        <DatePicker
          id="afm-range-to"
          labelText="To"
          value={max}
          minDate={toBoundDate(min)}
          invalid={invalid}
          onChange={(dates: Date[]) =>
            onChange({ ...range, max: toIso(dates) })
          }
        />
      </div>
    </>
  );
}

/**
 * The active attribute's current selection, shown as removable tags above the
 * editor: one cool-gray tag per categorical value, or a single tag summarizing the
 * range.
 *
 * @param {object} props - The kind, the draft, and the remove handlers.
 * @return {JSX.Element} The selected-values summary.
 */
function SelectedSummary({
  kind,
  draft,
  colorEnabled,
  valueLabel,
  onRemoveValue,
  onClearRange,
  onValueColorChange,
  onRangeColorChange,
}: {
  kind: FilterKind;
  draft: AttributeDraft;
  colorEnabled: boolean;
  /** Display text for one raw selected value. Identity when the caller supplies none. */
  valueLabel: (value: string) => string;
  onRemoveValue: (value: string) => void;
  onClearRange: () => void;
  onValueColorChange: (value: string, color: string) => void;
  onRangeColorChange: (color: string) => void;
}) {
  if (kind === 'categorical') {
    const values = draft.selectedValues ?? [];
    if (values.length === 0) {
      return <p className={styles.emptyHint}>No values selected</p>;
    }
    return (
      <div className={styles.selectedTags}>
        {values.map((value) => (
          <span key={value} className={styles.tagWithSwatch}>
            {colorEnabled && (
              // The swatch self-portals its popover and reports (color, opacity);
              // P2 has no opacity control, so the second arg is ignored.
              <ColorSwatch
                color={draft.valueColors?.[value] ?? DEFAULT_COLORS[0]}
                onChange={(color) => onValueColorChange(value, color)}
              />
            )}
            <DismissibleTag
              type="cool-gray"
              text={valueLabel(value)}
              onClose={() => onRemoveValue(value)}
              dismissTooltipLabel={`Remove ${valueLabel(value)}`}
            />
          </span>
        ))}
      </div>
    );
  }

  const summary = draft.range ? formatRangeSummary(draft.range, kind) : '';
  if (!summary) {
    return <p className={styles.emptyHint}>No range set</p>;
  }
  return (
    <div className={styles.selectedTags}>
      <span className={styles.tagWithSwatch}>
        {colorEnabled && (
          <ColorSwatch
            color={draft.rangeColor ?? DEFAULT_COLORS[0]}
            onChange={(color) => onRangeColorChange(color)}
          />
        )}
        <DismissibleTag
          type="cool-gray"
          text={summary}
          onClose={onClearRange}
          dismissTooltipLabel="Clear range"
        />
      </span>
    </div>
  );
}

/**
 * Right pane (color mode only): the "Apply to" targets as checkboxes, bound to the
 * active attribute's `applyTo`. The modal never names the targets — it renders
 * whatever `{ id, label }[]` the caller injected and emits the checked ids on the
 * selection (`FilterSelection.applyTo`). P2 passes a single target (Map),
 * default-checked.
 *
 * @param {object} props - The injected targets, the active applyTo, and the toggle handler.
 * @return {JSX.Element} The Apply-to checkbox pane.
 */
function ApplyToPane({
  targets,
  applyTo,
  onToggle,
}: {
  targets: { id: string; label: string }[];
  applyTo: string[];
  onToggle: (targetId: string, checked: boolean) => void;
}) {
  return (
    <div className={styles.applyToPane}>
      <h5 className={styles.applyToTitle}>Apply to</h5>
      {targets.map((target) => (
        <Checkbox
          key={target.id}
          id={`afm-target-${target.id}`}
          labelText={target.label}
          checked={applyTo.includes(target.id)}
          onChange={(_event, { checked }: { checked: boolean }) =>
            onToggle(target.id, checked)
          }
        />
      ))}
    </div>
  );
}

export interface AttributeFilterModalProps {
  /** Modal title noun supplied by the caller, e.g. "Filter wells". */
  title: string;
  /** Attributes to list, each with how to render its value editor. */
  attributes: FilterAttributeOption[];
  /** Show the per-value color picker + "Apply to" pane. Consumed in color mode (P2-T17). */
  colorEnabled: boolean;
  /** Color-mode "Apply to" targets (opaque ids). Consumed in P2-T17. */
  targets?: { id: string; label: string }[];
  /**
   * Pre-load the dataset's currently-applied selections. Read ONCE at mount (the
   * draft reducer seeds from it); the caller conditionally mounts the modal, so each
   * open is a fresh mount that re-seeds — it does NOT react to changes while mounted.
   */
  initialSelections?: FilterSelection[];
  /** Emits the create/update selections + the cleared-to-empty removals on Apply. */
  onApply: (result: AttributeFilterApply) => void;
  /** Close the modal without applying. */
  onClose: () => void;
}

/**
 * The mock §11b attribute-filter modal — a **domain-agnostic** editor. It's handed
 * a list of attributes (each with how to render its value editor) and the dataset's
 * current selections; it hands back the user's `{ selections, removed }` on Apply.
 * It knows nothing about wells/units/assets — all dataset semantics live in the
 * caller (`PageControlsPanel`). This slice (P2-T8) ships the **filter-mode** path;
 * the color swatches + "Apply to" pane (`colorEnabled`/`targets`) land in P2-T17.
 *
 * The user's in-progress picks live in a local `useReducer` draft (discarded on
 * Cancel), retained per attribute as they switch the left list. Apply is enabled
 * once there's something to commit — ≥1 attribute with a value/range, or ≥1
 * pre-loaded attribute cleared to empty (a removal). **Clear** empties the active
 * attribute; **Reset** empties them all.
 *
 * @param {AttributeFilterModalProps} props - The modal inputs/outputs.
 * @return {JSX.Element} The composed filter modal.
 */
export function AttributeFilterModal({
  title,
  attributes,
  colorEnabled,
  targets = [],
  initialSelections = [],
  onApply,
  onClose,
}: AttributeFilterModalProps) {
  const optionByAttribute = useMemo(
    () => new Map(attributes.map((item) => [item.attribute, item])),
    [attributes]
  );

  // Attributes that were pre-loaded — any of these the user empties becomes a
  // `removed` tombstone on Apply (so clear-and-Apply deletes its card).
  const preloaded = useMemo(
    () => new Set(initialSelections.map((selection) => selection.attribute)),
    [initialSelections]
  );

  // All injected target ids — the color-mode default (every target checked, so Map
  // is on out of the box). A selection's `applyTo` falls back to this until the user
  // unchecks one.
  const defaultTargetIds = useMemo(
    () => targets.map((target) => target.id),
    [targets]
  );

  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    // Never default-activate a disabled attribute — fall to the first enabled one
    // (a pre-loaded selection is always enabled, so it's safe to prefer).
    active:
      initialSelections[0]?.attribute ??
      attributes.find((item) => !item.disabled)?.attribute ??
      null,
    draft: seedDraft(initialSelections),
  }));

  const { active, draft } = state;
  const activeOption = active
    ? optionByAttribute.get(active)?.option
    : undefined;
  const activeDraft: AttributeDraft = (active && draft[active]) || {};

  // How the ACTIVE attribute's values are displayed. Scoped per attribute by the
  // caller, so only the attributes with a non-human stored vocabulary (wellStatus)
  // are relabelled — operators, formations and the rest still render verbatim.
  // Identity when the caller supplies none; memoized so it's a stable dependency
  // for the editor's memoized scans.
  const activeValueLabel = useMemo(
    () => (active && optionByAttribute.get(active)?.valueLabel) || identity,
    [active, optionByAttribute]
  );

  const setActiveValues = (values: string[]) => {
    if (!active) {
      return;
    }
    if (!colorEnabled) {
      dispatch({ type: 'setValues', attribute: active, values });
      return;
    }
    // Color mode: every value carries a color. Preserve prior picks and auto-assign
    // each newly-selected value a default by its position (`DEFAULT_COLORS[i % 24]`),
    // overridable via its swatch — so Apply is never gated on "pick a color".
    const prev = activeDraft.valueColors ?? {};
    const valueColors: Record<string, string> = {};
    values.forEach((value, index) => {
      valueColors[value] =
        prev[value] ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    });
    dispatch({ type: 'setValues', attribute: active, values, valueColors });
  };

  const setActiveRange = (range: FilterRange) => {
    if (!active) {
      return;
    }
    if (!colorEnabled) {
      dispatch({ type: 'setRange', attribute: active, range });
      return;
    }
    // Color mode: a range carries one color — auto-assign the first default once a
    // bound is set (keeping any prior pick), overridable via the swatch.
    const hasBound = isBoundSet(range.min) || isBoundSet(range.max);
    const rangeColor =
      activeDraft.rangeColor ?? (hasBound ? DEFAULT_COLORS[0] : undefined);
    dispatch({ type: 'setRange', attribute: active, range, rangeColor });
  };

  const setActiveValueColor = (value: string, color: string) => {
    if (active) {
      dispatch({ type: 'setValueColor', attribute: active, value, color });
    }
  };
  const setActiveRangeColor = (color: string) => {
    if (active) {
      dispatch({ type: 'setRangeColor', attribute: active, color });
    }
  };

  // Toggle one "Apply to" target for the active attribute. Falls back to the
  // all-checked default before the user's first toggle.
  const toggleActiveTarget = (targetId: string, checked: boolean) => {
    if (!active) {
      return;
    }
    const current = activeDraft.applyTo ?? defaultTargetIds;
    const next = checked
      ? [...new Set([...current, targetId])]
      : current.filter((id) => id !== targetId);
    dispatch({ type: 'setApplyTo', attribute: active, applyTo: next });
  };

  // The Apply payload: every touched attribute with ≥1 value/bound is a selection;
  // every pre-loaded attribute now empty is a removal.
  const { selections, removed } = useMemo(() => {
    const built: FilterSelection[] = [];
    Object.entries(draft).forEach(([attribute, entry]) => {
      const item = optionByAttribute.get(attribute);
      const option = item?.option;
      // Defensively drop disabled attributes — they can't be activated, so this
      // only fires if one was seeded via initialSelections; never commit a rule the
      // read endpoints would reject.
      if (!option || item?.disabled || !hasSelection(entry)) {
        return;
      }
      built.push({
        attribute,
        kind: option.type,
        selectedValues: entry.selectedValues,
        range: entry.range,
        // Color fields only in color mode; `applyTo` falls back to all targets, so a
        // created/updated rule is on the Map by default.
        ...(colorEnabled
          ? {
              valueColors: entry.valueColors,
              rangeColor: entry.rangeColor,
              applyTo: entry.applyTo ?? defaultTargetIds,
            }
          : {}),
      });
    });
    const touched = new Set(built.map((selection) => selection.attribute));
    return {
      selections: built,
      removed: [...preloaded].filter((attribute) => !touched.has(attribute)),
    };
  }, [draft, optionByAttribute, preloaded, colorEnabled, defaultTargetIds]);

  // A range with min > max (or From > To) can't be applied. Checked across the
  // WHOLE draft — an invalid range on a non-active attribute would otherwise still
  // be committed — and surfaced inline on the active editor below.
  const hasInvalidRange = useMemo(
    () =>
      Object.entries(draft).some(([attribute, entry]) =>
        isRangeInvalid(
          entry.range,
          optionByAttribute.get(attribute)?.option.type ?? 'categorical'
        )
      ),
    [draft, optionByAttribute]
  );

  const canApply =
    (selections.length > 0 || removed.length > 0) &&
    !hasInvalidRange &&
    // Color mode: every created/updated rule needs ≥1 target (Map is checked by
    // default, so this holds out of the box unless the user unchecks every target).
    (!colorEnabled ||
      selections.every((selection) => (selection.applyTo?.length ?? 0) > 0));

  return (
    <ComposedModal open onClose={onClose} size="lg">
      <ModalHeader title={title} />
      <ModalBody
        className={classNames(styles.content, {
          [styles.contentColor]: colorEnabled,
        })}
      >
        <AttributeList
          attributes={attributes}
          active={active}
          draft={draft}
          onActivate={(attribute) => dispatch({ type: 'activate', attribute })}
        />
        <div className={styles.editor}>
          {active && activeOption ? (
            <>
              <h4 className={styles.editorTitle}>
                {optionByAttribute.get(active)?.label}
              </h4>
              <SelectedSummary
                kind={activeOption.type}
                draft={activeDraft}
                colorEnabled={colorEnabled}
                valueLabel={activeValueLabel}
                onRemoveValue={(value) =>
                  setActiveValues(
                    (activeDraft.selectedValues ?? []).filter(
                      (other) => other !== value
                    )
                  )
                }
                onClearRange={() => dispatch({ type: 'clearActive' })}
                onValueColorChange={setActiveValueColor}
                onRangeColorChange={setActiveRangeColor}
              />
              {activeOption.type === 'categorical' && (
                <CategoricalEditor
                  key={active}
                  label={optionByAttribute.get(active)?.label ?? active}
                  values={activeOption.values}
                  selected={activeDraft.selectedValues ?? []}
                  onChange={setActiveValues}
                  valueLabel={activeValueLabel}
                  hideBulkSelect={colorEnabled}
                />
              )}
              {activeOption.type === 'numericRange' && (
                <NumericRangeEditor
                  range={activeDraft.range ?? {}}
                  invalid={isRangeInvalid(activeDraft.range, 'numericRange')}
                  onChange={setActiveRange}
                />
              )}
              {activeOption.type === 'dateRange' && (
                <DateRangeEditor
                  range={activeDraft.range ?? {}}
                  invalid={isRangeInvalid(activeDraft.range, 'dateRange')}
                  onChange={setActiveRange}
                />
              )}
              <div className={styles.clearRow}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => dispatch({ type: 'clearActive' })}
                  disabled={!hasSelection(activeDraft)}
                >
                  Clear
                </button>
              </div>
            </>
          ) : (
            <p className={styles.emptyHint}>No attributes to filter.</p>
          )}
        </div>
        {colorEnabled && (
          <ApplyToPane
            targets={targets}
            applyTo={activeDraft.applyTo ?? defaultTargetIds}
            onToggle={toggleActiveTarget}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button kind="ghost" onClick={() => dispatch({ type: 'reset' })}>
          Reset
        </Button>
        <Button
          kind="primary"
          disabled={!canApply}
          onClick={() => onApply({ selections, removed })}
        >
          Apply Filters
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}
